import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { find } from 'lodash';
import StarIcon from '@mui/icons-material/Star';
import moment from 'moment';

import { LeaderboardConfigFragment, UserSegmentFragment } from 'api';

interface Props {
  config: LeaderboardConfigFragment;
  segments: UserSegmentFragment[];
}

const View: FC<Props> = props => {
  const { t } = useTranslation();

  const { config, segments } = props;
  const { all_users, description, segment_ids, start_at, end_at } = config;

  function renderSegmentNames() {
    if (all_users) {
      return (
        <Fragment>
          <em>{t('components:allActiveUsers')}</em>
        </Fragment>
      );
    }

    const segmentList = segment_ids.reduce((list, id) => {
      const segment = find(segments, ['id', id]);
      return list.concat(segment ? [segment.title] : []);
    }, []);

    if (!segmentList.length) {
      return (
        <Fragment>
          <em>{t('admin:leaderboardsSegmentsMissing')}</em>
        </Fragment>
      );
    }

    return segmentList.join(', ');
  }

  function renderDuration() {
    if (all_users) {
      return <em>{t('leaderboard:timeframe')}</em>;
    }
    const clientTz = moment.tz.guess();
    const startDate = !!start_at && moment.unix(start_at).tz(clientTz).format('LLL z');
    const endDate = !!end_at && moment.unix(end_at).tz(clientTz).format('LLL z');

    const includeSeparator = !!start_at && !!end_at;

    if (!startDate && !endDate) {
      return <em>{t('admin:leaderboardsDurationMissing')}</em>;
    }

    return (
      <Fragment>
        {startDate}
        {'   '}
        {includeSeparator && (
          <Box component="span" sx={{ textTransform: 'lowercase' }}>
            {t('common:to')}
          </Box>
        )}{' '}
        {endDate}
      </Fragment>
    );
  }

  function renderDetail() {
    return (
      <Grid xs={12} container alignItems="center">
        <Grid xs={6}>
          <Typography variant="subtitle2">{t('common:segment_plural')}</Typography>
          <Typography color="textSecondary">{renderSegmentNames()}</Typography>
        </Grid>
        <Grid xs={6}>
          <Typography variant="subtitle2">{t('common:duration')}</Typography>
          <Typography color="textSecondary">{renderDuration()}</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <Divider sx={{ margin: theme => theme.spacing(1.5, -2, 3, -2) }} />
      <Grid xs={12} container alignItems="center" spacing={2}>
        <Grid>
          <StarIcon sx={{ color: 'transparent' }} />
        </Grid>
        <Grid xs={10} container direction="column" wrap="nowrap" spacing={3}>
          {!!description && (
            <Grid container>
              <Grid xs>
                <Typography variant="subtitle2">{t('common:description')}</Typography>
                <Typography color="textSecondary">{description}</Typography>
              </Grid>
            </Grid>
          )}
          {renderDetail()}
        </Grid>
        <Grid>
          <StarIcon sx={{ color: 'transparent' }} />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default View;
